import { type AggResponse, fetchAgg } from '../../../common/utils/fetch-agg';
import { inMemoryCache } from '../../../utils/caching/in-memory';

export const positionEmploymentTypesCache: {
	get: (key: string) => Promise<AggResponse<PositionEmploymentTypesResponse>> | undefined;
	set: (key: string, result: Promise<AggResponse<PositionEmploymentTypesResponse>>) => void;
	has: (key: string) => boolean;
	clear: (key?: string) => void;
	inMemoryDecorator: (
		key: string,
		supplier: () => Promise<AggResponse<PositionEmploymentTypesResponse>>,
		expireWhen?: ((result: AggResponse<PositionEmploymentTypesResponse>) => boolean) | undefined,
	) => Promise<AggResponse<PositionEmploymentTypesResponse>>;
} = inMemoryCache<AggResponse<PositionEmploymentTypesResponse>>();

const QUERY_NAME = 'PositionEmploymentTypes';
const positionEmploymentTypesQueryString = `query ${QUERY_NAME}($cloudId: ID!, $fieldId: ID!, $rql: String, $after: String, $first: Int) {
  radar_fieldValues(
    cloudId: $cloudId
    uniqueFieldId: $fieldId
    rql: $rql
    first: $first
    after: $after
  ) { 
    totalCount
    pageInfo {
      endCursor
      hasNextPage
    }
    edges {
      cursor
      node {
        ... on RadarStringFieldValue {
          __typename
          isRestricted
          stringValue: value
        }
      }
    }
  }
}`;

export type PositionEmploymentType = {
	cursor: string;
	node: {
		isRestricted: boolean;
		stringValue: string;
	};
};

export type PositionEmploymentTypesResponse = {
	radar_fieldValues: {
		edges?: PositionEmploymentType[];
	};
};

export type PositionEmploymentTypesQueryVariables = {
	cloudId: string;
	first: number;
	fieldId: string;
	rql: string;
	after?: string;
};

export const positionEmploymentTypesQuery: (
	cloudId: string,
	rql: string,
	first?: number,
	after?: string,
	headers?: Record<string, string>,
) => Promise<AggResponse<PositionEmploymentTypesResponse>> = (
	cloudId: string,
	rql: string,
	first = 20,
	after?: string,
	headers?: Record<string, string>,
) => {
	const fieldId = 'positionEmploymentType';
	return positionEmploymentTypesCache.inMemoryDecorator(
		[cloudId, first, rql, fieldId, after].filter(Boolean).join('-'),
		async () => {
			if (!cloudId) {
				return {
					data: {
						radar_fieldValues: {
							edges: [],
						},
					},
				};
			}

			const response = await fetchAgg<
				PositionEmploymentTypesQueryVariables,
				PositionEmploymentTypesResponse
			>({
				graphQuery: positionEmploymentTypesQueryString,
				variables: { cloudId, first, rql, fieldId, after },
				operationName: QUERY_NAME,
				headers,
			});

			const hasErrors = response.errors?.some((error) => error);

			if (hasErrors) {
				return Promise.reject(response);
			}
			return response;
		},
	);
};
